import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, CalendarBlank, CheckCircle } from '@phosphor-icons/react';
import { PRO_TOKEN_KEY } from '@/pages/wellness/WellnessProLoginPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DAYS = [
  { id: 0, label: 'Mon' }, { id: 1, label: 'Tue' }, { id: 2, label: 'Wed' },
  { id: 3, label: 'Thu' }, { id: 4, label: 'Fri' }, { id: 5, label: 'Sat' }, { id: 6, label: 'Sun' },
];

const proAxios = () => axios.create({
  baseURL: BACKEND_URL,
  headers: { Authorization: `Bearer ${localStorage.getItem(PRO_TOKEN_KEY) || ''}` },
});

const AvailabilityTab = () => {
  const [av, setAv] = useState({
    timezone_name: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    working_days: [0, 1, 2, 3, 4],
    start_time: '09:00',
    end_time: '18:00',
    consultation_duration_min: 45,
    buffer_min: 15,
    exceptions: [],
  });
  const [saving, setSaving] = useState(false);
  const [exception, setException] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await proAxios().get('/api/wellness/pro/availability');
      const d = res.data;
      setAv({
        timezone_name: d.timezone_name || 'UTC',
        working_days: d.working_days || [],
        start_time: d.working_hours?.start || '09:00',
        end_time: d.working_hours?.end || '18:00',
        consultation_duration_min: d.consultation_duration_min || 45,
        buffer_min: d.buffer_min || 15,
        exceptions: d.exceptions || [],
      });
    } catch (err) { /* first load — use defaults */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleDay = (d) => setAv((s) => ({ ...s, working_days: s.working_days.includes(d) ? s.working_days.filter((x) => x !== d) : [...s.working_days, d].sort() }));

  const addException = () => {
    if (!exception) return;
    if (av.exceptions.includes(exception)) { toast.error('Already added'); return; }
    setAv((s) => ({ ...s, exceptions: [...s.exceptions, exception].sort() }));
    setException('');
  };

  const removeException = (d) => setAv((s) => ({ ...s, exceptions: s.exceptions.filter((x) => x !== d) }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await proAxios().put('/api/wellness/pro/availability', {
        ...av,
        consultation_duration_min: parseInt(av.consultation_duration_min) || 45,
        buffer_min: parseInt(av.buffer_min) || 15,
      });
      toast.success('Availability saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const inputCls = "w-full bg-white border border-stone-200 focus:border-[#1A4331] focus:ring-4 focus:ring-[#1A4331]/10 rounded-2xl px-4 py-3 outline-none transition-all text-[15px] text-[#1C1917]";
  const labelCls = "block text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500 mb-1.5";

  return (
    <div className="space-y-6" data-testid="pro-availability-tab">
      <div>
        <h2 className="text-xl font-semibold font-heading text-[#1C1917]">Availability</h2>
        <p className="text-sm text-stone-500 mt-0.5">Set your working days, hours and session length. Clients will only see available slots.</p>
      </div>

      <div className="bg-white rounded-3xl p-6 border border-stone-100 space-y-5">
        <div>
          <label className={labelCls}>Timezone</label>
          <input value={av.timezone_name} onChange={(e) => setAv({ ...av, timezone_name: e.target.value })} className={inputCls} data-testid="av-timezone" />
        </div>

        <div>
          <label className={labelCls}>Working Days</label>
          <div className="flex gap-1.5 flex-wrap">
            {DAYS.map((d) => (
              <button key={d.id} type="button" onClick={() => toggleDay(d.id)} className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${av.working_days.includes(d.id) ? 'bg-[#1A4331] text-white border-[#1A4331]' : 'bg-white text-stone-700 border-stone-200 hover:border-[#1A4331]'}`} data-testid={`av-day-${d.id}`}>
                {d.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>Start Time</label><input type="time" value={av.start_time} onChange={(e) => setAv({ ...av, start_time: e.target.value })} className={inputCls} data-testid="av-start" /></div>
          <div><label className={labelCls}>End Time</label><input type="time" value={av.end_time} onChange={(e) => setAv({ ...av, end_time: e.target.value })} className={inputCls} data-testid="av-end" /></div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>Session Duration (min)</label><input type="number" min={15} max={240} value={av.consultation_duration_min} onChange={(e) => setAv({ ...av, consultation_duration_min: e.target.value })} className={inputCls} data-testid="av-duration" /></div>
          <div><label className={labelCls}>Buffer Between (min)</label><input type="number" min={0} max={120} value={av.buffer_min} onChange={(e) => setAv({ ...av, buffer_min: e.target.value })} className={inputCls} /></div>
        </div>

        <div>
          <label className={labelCls}>Blocked Dates</label>
          <p className="text-xs text-stone-500 mb-2">Specific days you&apos;re unavailable (e.g., holidays).</p>
          <div className="flex gap-2 mb-2">
            <input type="date" value={exception} onChange={(e) => setException(e.target.value)} className={`${inputCls} flex-1`} data-testid="av-exception-input" />
            <button onClick={addException} className="bg-[#1A4331] text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#2A5A44]" data-testid="av-exception-add">Add</button>
          </div>
          {av.exceptions.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {av.exceptions.map((e) => (
                <span key={e} className="inline-flex items-center gap-1 bg-stone-50 border border-stone-200 text-stone-700 px-2.5 py-1 rounded-full text-xs">
                  {e} <button onClick={() => removeException(e)} className="text-red-500 hover:text-red-700 ml-1">×</button>
                </span>
              ))}
            </div>
          )}
        </div>

        <button onClick={handleSave} disabled={saving} className="bg-[#1A4331] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#2A5A44] disabled:opacity-50 inline-flex items-center gap-2" data-testid="av-save-btn">
          {saving ? 'Saving...' : (<><CheckCircle size={16} weight="duotone" /> Save Availability</>)}
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-2xl p-4 text-xs flex gap-2">
        <CalendarBlank size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
        <p><strong>How slot generation works:</strong> based on your working days &amp; hours, we generate slots of {av.consultation_duration_min} min with a {av.buffer_min} min buffer. Clients see only unbooked, future slots. Blocked dates are excluded automatically.</p>
      </div>
    </div>
  );
};

export default AvailabilityTab;
